import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon } from '@mui/icons-material';
import InviteSupplierModal from '../components/InviteSupplierModal';
import { GhgSupplierSurveyWithCountry, useSupplierSurvey } from '../hooks';
import { toast } from 'react-toastify';
import ConfirmationModal from 'src/components/ConfirmationModal';
import {
  GhgSupplierSurveyAnswerSchema,
  GhgSupplierSurveySummary,
} from '../../interfaces';
import { format } from 'date-fns';
import {
  useElementsPagination,
  useGhgOrganization,
} from '../../../common/hooks';
import TableHeader from 'src/Ghg/components/TableV2/TableHeader';
import ButtonsCell from 'src/Ghg/components/TableV2/ButtonsCell';
import themeColors from 'src/theme/colors';
import { StyledPanel } from 'src/components/StyledPanel';
import { StyledTableV2Cell } from 'src/Ghg/components/TableV2/styles';
import PreviewDialog from './PreviewDialog';
import CopyLink from 'src/Ghg/components/CopyLink';
import { getRabenPeriodStart } from 'src/utils/timeConverter';
import { useBranchesWithCountry } from 'src/utils/branches';
import DelayedTextField from 'src/components/DelayedTextField';
import ElementsPagination from 'src/components/Pagination';
import CountryPicker, {
  CountryPickerOption,
} from 'src/components/CountryPicker';

const T_KEY = 'ghg.scope3GoodsAndServices.productsFromSuppliers';
const dateFormat = 'yyyy-MM-dd';

export default function ProductsFromSuppliers() {
  const { t } = useTranslation();

  const [deleteInvitation, setDeleteInvitation] =
    React.useState<GhgSupplierSurveyWithCountry | null>(null);

  const organization = useGhgOrganization();

  const [newInvitation, setNewInvitation] = React.useState<boolean>(false);
  const [editInvitation, setEditInvitation] =
    React.useState<GhgSupplierSurveyWithCountry | null>(null);

  const [previewAnswerId, setPreviewAnswerId] = React.useState<number>();
  const [previewOpen, setPreviewOpen] = React.useState<boolean>(false);

  const defaultYear = String(getRabenPeriodStart().getFullYear());
  const [reportingYear, setReportingYear] = React.useState<string>(defaultYear);

  // Filters row (Goal 1). Oddział stays single-select on purpose: plain
  // text values, no chips/pills (Guardrail 1). Kraj is now the shared
  // multi-select CountryPicker (specs/001-raben-country-masteradmin/
  // mass-specs/specs/20260826-country-multiselect-picker.md) -- the backend
  // already accepted `countries` as a list here (C06), so a single
  // selection is just the one-element-list case it always was.
  const [search, setSearch] = React.useState<string>('');
  const [selectedCountries, setSelectedCountries] = React.useState<string[]>(
    []
  );
  const [selectedBranch, setSelectedBranch] = React.useState<string>('');

  // R-06: server paging (EC-2) -- default page size (20) matches the
  // backend's own default and keeps first paint well under the ~50-row
  // budget even for a 360-row account.
  const { page, setPage, elementsPerPage, setElementsPerPage } =
    useElementsPagination(1, 20);

  const { branches: branchesWithCountry } = useBranchesWithCountry();

  const countryOptions: CountryPickerOption[] = React.useMemo(() => {
    const byCode = new Map<string, string>();
    branchesWithCountry.forEach((branch) => {
      if (branch.country_code) {
        byCode.set(
          branch.country_code,
          branch.country_name || branch.country_code
        );
      }
    });
    return Array.from(byCode.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [branchesWithCountry]);

  const branchOptions = React.useMemo(() => {
    if (selectedCountries.length === 0) return branchesWithCountry;
    return branchesWithCountry.filter(
      (branch) =>
        !!branch.country_code && selectedCountries.includes(branch.country_code)
    );
  }, [branchesWithCountry, selectedCountries]);

  // A country change can drop the currently selected branch out of its own
  // option list -- clear it instead of silently keeping a hidden filter.
  React.useEffect(() => {
    if (
      selectedBranch &&
      !branchOptions.some((branch) => branch.name === selectedBranch)
    ) {
      setSelectedBranch('');
    }
  }, [branchOptions, selectedBranch]);

  React.useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    selectedCountries,
    selectedBranch,
    reportingYear,
    elementsPerPage,
  ]);

  // EC-5: an empty selection must send NO `countries` param at all -- not
  // an empty array -- so the legacy no-filter path is used server-side.
  const countriesParam = selectedCountries.length
    ? selectedCountries
    : undefined;
  const branchesParam = selectedBranch ? [selectedBranch] : undefined;

  const {
    data: surveys,
    total,
    remove,
    summary,
  } = useSupplierSurvey(
    organization.id,
    reportingYear,
    reportingYear,
    branchesParam,
    countriesParam,
    search || undefined,
    page,
    elementsPerPage
  );

  // F-01: from_period/to_period now live on the base filter schema (backend
  // d615e3953), so the list endpoint filters by year server-side again --
  // "Rok" restricts which rows are fetched here exactly like it already did
  // for `summary` below and for exports. `availableYears` still can't be
  // derived from `data`, though: `data` is only the current page of the
  // CURRENTLY SELECTED year (that's the whole point of sending from/to
  // above), so every row in it would always report the same year. A fixed,
  // small recent window is the fallback in the absence of a dedicated
  // "years with data" endpoint.
  const availableYears = React.useMemo(() => {
    const start = Number(defaultYear);
    const years = new Set<string>([defaultYear, reportingYear]);
    for (let offset = 0; offset <= 4; offset += 1)
      years.add(String(start - offset));
    return Array.from(years).sort((a, b) => Number(b) - Number(a));
  }, [defaultYear, reportingYear]);

  const handleSendAgainClick = (
    invitation: GhgSupplierSurveyWithCountry | null
  ) => setEditInvitation(invitation);

  const handleDeleteClick = (invitation: GhgSupplierSurveyWithCountry) =>
    setDeleteInvitation(invitation);

  const handleDeleteInvitation = () =>
    remove.mutateAsync(deleteInvitation?.id as number).then(() => {
      toast.success(
        t('ghg.scope3GoodsAndServices.toasts.invitationDeleted') as string
      );
      setDeleteInvitation(null);
    });

  const formatDate = (date: string) => {
    return format(new Date(date), dateFormat);
  };

  const formatSentAt = (sentAt: string) => {
    return `${t(`${T_KEY}.table.sent`)}: ${formatDate(sentAt)}`;
  };

  const isCalculatorCompleted = (progressPercentage: number | null) =>
    progressPercentage !== null && progressPercentage >= 100;

  const summaryByVatAndPeriod = React.useMemo(() => {
    const map = new Map<string, GhgSupplierSurveySummary>();
    (summary.data || []).forEach((s) => {
      map.set(`${s.vat_identifier}-${s.reporting_period}`, s);
    });
    return map;
  }, [summary.data]);

  const formatSubmittedAt = (
    row: GhgSupplierSurveyWithCountry,
    answer: GhgSupplierSurveyAnswerSchema | null
  ) => {
    if (!answer) {
      return t(`${T_KEY}.table.noResponse`);
    }
    if (!answer.is_submitted) {
      return t(`${T_KEY}.table.partialResponse`);
    }

    if (answer.wants_to_use_platform) {
      const progressPercentage = summaryByVatAndPeriod.get(
        `${row.vat_identifier}-${row.reporting_period}`
      )?.progress_percentage;
      // If summary isn't loaded (or the row isn't present), fall back to the
      // historical behavior to avoid flicker.
      if (progressPercentage !== undefined) {
        if (!isCalculatorCompleted(progressPercentage)) {
          return t(`${T_KEY}.table.partialResponsePlatformIncomplete`);
        }
      }
    }

    return formatDate(answer.submitted_at!);
  };

  // Empty state wording (Goal 1 / R-06 follow-up): with year filtering
  // restored, an empty table can mean two different things -- distinguish
  // them rather than always blaming the year.
  const hasActiveFilters =
    !!search.trim() || !!selectedCountries.length || !!selectedBranch;

  const headerKeys = [
    'vatIdentifier',
    'supplierName',
    'country',
    'branch',
    'supplierEmail',
    'suppliersResponse',
  ];
  const emptyColSpan = headerKeys.length + 1;

  return (
    <div style={{ minHeight: '500px', minWidth: '900px' }}>
      <Typography variant="h2" sx={{ mt: '32px', mb: '32px' }}>
        {t(`${T_KEY}.title`)}
      </Typography>

      <CopyLink organizationId={organization.id} mb="32px" />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '12px',
          mb: '12px',
        }}
      >
        <DelayedTextField
          label={t(`${T_KEY}.filters.searchLabel`)}
          size="small"
          onDelayedChange={setSearch}
          sx={{ minWidth: 220 }}
        />

        <CountryPicker
          options={countryOptions}
          value={selectedCountries}
          onChange={setSelectedCountries}
          label={String(t(`${T_KEY}.filters.countryLabel`))}
          ariaLabel={String(t(`${T_KEY}.filters.countryLabel`))}
          allSelectedLabel={String(t(`${T_KEY}.filters.allCountries`))}
          searchPlaceholder={String(t('ghg.countryPicker.searchPlaceholder'))}
          noResultsText={String(t('ghg.countryPicker.noResults'))}
          selectedSummaryTemplate={String(
            t('ghg.countryPicker.selectedSummary')
          )}
          sx={{ minWidth: 220 }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="supplier-surveys-branch-label">
            {t(`${T_KEY}.filters.branchLabel`)}
          </InputLabel>
          <Select
            labelId="supplier-surveys-branch-label"
            value={selectedBranch}
            label={t(`${T_KEY}.filters.branchLabel`) as string}
            onChange={(e) => setSelectedBranch(e.target.value)}
          >
            <MenuItem value="">{t(`${T_KEY}.filters.allBranches`)}</MenuItem>
            {branchOptions.map((branch) => (
              <MenuItem key={branch.name} value={branch.name}>
                {branch.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="supplier-surveys-reporting-year-label">
            {t(`${T_KEY}.filters.reportingYearLabel`)}
          </InputLabel>
          <Select
            labelId="supplier-surveys-reporting-year-label"
            value={reportingYear}
            label={t(`${T_KEY}.filters.reportingYearLabel`) as string}
            onChange={(e) => setReportingYear(e.target.value)}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <StyledPanel>
        <TableContainer>
          <Table>
            <TableHeader headerKeys={headerKeys} />
            <TableBody>
              {surveys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={emptyColSpan}>
                    <Typography sx={{ py: '18px' }}>
                      {hasActiveFilters
                        ? t(`${T_KEY}.filters.noResults`)
                        : t(`${T_KEY}.filters.emptyForYear`, {
                            year: reportingYear,
                          })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                surveys.map((row) => (
                  <TableRow key={row.id}>
                    <StyledTableV2Cell number={row.id}>
                      <Typography
                        variant="captionText"
                        color={themeColors.ghgDarkGrey}
                      >
                        {formatSentAt(row.created_at)}
                      </Typography>
                      <Typography>{row.vat_identifier}</Typography>
                    </StyledTableV2Cell>
                    <StyledTableV2Cell number={row.id}>
                      <Typography>{row.answer?.company_name}</Typography>
                    </StyledTableV2Cell>
                    <StyledTableV2Cell number={row.id}>
                      <Typography>
                        {(row.country_codes || []).join(', ')}
                      </Typography>
                    </StyledTableV2Cell>
                    <StyledTableV2Cell number={row.id}>
                      <Typography>
                        {(row.branch_descriptions || []).join(', ')}
                      </Typography>
                    </StyledTableV2Cell>
                    <StyledTableV2Cell number={row.id}>
                      <Typography>{row.answer?.contact_email}</Typography>
                    </StyledTableV2Cell>
                    <StyledTableV2Cell number={row.id}>
                      <Typography>
                        {formatSubmittedAt(row, row.answer)}
                      </Typography>
                    </StyledTableV2Cell>
                    <ButtonsCell
                      id={row.id}
                      onDelete={() => handleDeleteClick(row)}
                      onSendAgain={() => handleSendAgainClick(row)}
                      onPreview={
                        !!row.answer
                          ? () => {
                              setPreviewAnswerId(row.id);
                              setPreviewOpen(true);
                            }
                          : null
                      }
                    />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledPanel>

      {!!total && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: '16px',
          }}
        >
          <Typography
            variant="caption"
            color={themeColors.ghgDarkGrey}
            sx={{ mb: '8px' }}
          >
            {t(`${T_KEY}.filters.totalResults`, { count: total })}
          </Typography>
          <ElementsPagination
            totalElements={total}
            elementsPerPage={elementsPerPage}
            setElementsPerPage={setElementsPerPage}
            page={page}
            setPage={setPage}
          />
        </Box>
      )}

      <Button
        variant="contained"
        sx={{ mt: '25px' }}
        startIcon={
          <AddCircleOutlineOutlinedIcon sx={{ transform: 'scale(1.2)' }} />
        }
        onClick={() => setNewInvitation(true)}
      >
        {t(`goods-and-services.question1.method2.button`)}
      </Button>
      <InviteSupplierModal
        key={editInvitation?.id}
        open={!!newInvitation || !!editInvitation}
        onClose={() => {
          setNewInvitation(false);
          setEditInvitation(null);
        }}
        invitation={editInvitation}
        organizationId={organization.id}
      />

      <ConfirmationModal
        open={!!deleteInvitation}
        onClose={() => setDeleteInvitation(null)}
        onFalse={() => setDeleteInvitation(null)}
        onTrue={handleDeleteInvitation}
        titleKey={`${T_KEY}.table.deleteConfirmation.title`}
        textKey={`${T_KEY}.table.deleteConfirmation.text`}
      />

      <PreviewDialog
        id={previewAnswerId}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        organizationId={organization.id}
      />
    </div>
  );
}
